/**
 * New Lease wizard.
 *
 * Replaces the old single-shot `LeaseForm` at `/staff/leases/new`. Nothing is
 * written to the database until the final step: the lease terms, the
 * household entered for the primary tenant, and the selected addendums all
 * live in local component state until "Create lease" runs the real sequence
 * of API calls. The preview step's PDF is generated from that same
 * in-progress state with nothing persisted — see `POST /api/leases/preview`.
 *
 * The generated lease contract is a PLACEHOLDER, not attorney-reviewed — see
 * `lib/lease-pdf.ts` on the API and "Document and Email Generators" in
 * CLAUDE.md. It exists so the mechanism (fill in terms, preview, print for
 * signing) can be exercised end to end before the firm's real template
 * exists.
 *
 * "Pre-existing lease" (step 1) follows the same rule — the real PDF stays a
 * local `File` until "Create lease" uploads it, same as everything else
 * here. A pre-existing lease skips the placeholder contract above entirely
 * (the real one just uploaded supersedes it) and, when an opening balance is
 * entered, records it as a charge in the same request that creates the
 * lease — see `createLease` in the API's `modules/leases/service.ts`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useList } from '@refinedev/core';
import { LEASE_STATUSES, maxSecurityDeposit } from 'shared';
import { api } from '../../../lib/api.js';
import { uploadDocument } from '../../../lib/dataProvider.js';
import { useCan } from '../../../lib/capabilities.js';
import { Field, FormErrors, Select, TextInput, fieldErrors } from '../../../components/Form.js';
import { formatBytes } from '../../../components/PhotoUpload.js';

/** A pre-existing lease's real signed contract — PDF only, unlike PhotoUpload's broader accept list. */
const LEASE_PDF_ACCEPT = 'application/pdf';
/** Mirrors MAX_UPLOAD_BYTES on the API. Checked here only to fail fast before a round trip. */
const LEASE_PDF_MAX_BYTES = 25 * 1024 * 1024;

interface UnitOption {
  id: string;
  label: string;
  propertyName?: string;
  marketRent: string;
  status: string;
}

interface TenantOption {
  id: string;
  firstName: string;
  lastName: string;
}

interface LeaseAddendumOption {
  id: string;
  name: string;
  body: string;
}

interface DraftDependent {
  name: string;
  relationship: string;
}

interface DraftPet {
  name: string;
  species: string;
  breed: string;
}

const STEPS = ['Terms', 'Household', 'Addendums', 'Preview & create'] as const;

/** Raw fetch, not the `api()` helper — the response here is a PDF binary, not JSON. */
async function fetchPreviewPdf(payload: unknown): Promise<Blob> {
  const response = await fetch('/api/leases/preview', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let message = response.statusText;
    try {
      const body = (await response.json()) as { error?: { message?: string } };
      message = body.error?.message ?? message;
    } catch {
      // A non-JSON error body means the request never reached Fastify.
    }
    throw new Error(message);
  }
  return response.blob();
}

export function LeaseWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  // Step 1: lease terms — same fields LeaseForm used to submit directly.
  const [unitId, setUnitId] = useState('');
  const [status, setStatus] = useState('DRAFT');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');
  const [monthlyRent, setMonthlyRent] = useState('');
  const [securityDeposit, setSecurityDeposit] = useState('');
  const [rentDueDay, setRentDueDay] = useState('1');
  const [notes, setNotes] = useState('');
  // Order matters: the first tenant checked is primary.
  const [tenantIds, setTenantIds] = useState<string[]>([]);

  // Pre-existing lease onboarding — a lease that existed before this system
  // was in use. Same "nothing written until Create lease" rule as the rest
  // of this wizard: the PDF is captured as a File, not uploaded, until then.
  const [isPreExisting, setIsPreExisting] = useState(false);
  const [preExistingFile, setPreExistingFile] = useState<File | null>(null);
  const [preExistingFileError, setPreExistingFileError] = useState<string | null>(null);
  const preExistingFileInputRef = useRef<HTMLInputElement>(null);
  const [openingBalanceAmount, setOpeningBalanceAmount] = useState('');
  const [deferFirstMonthRent, setDeferFirstMonthRent] = useState(false);
  const canWriteLedger = useCan('ledger:write');

  function handlePreExistingFile(file: File) {
    setPreExistingFileError(null);
    if (file.size > LEASE_PDF_MAX_BYTES) {
      setPreExistingFileError(
        `That file is ${formatBytes(file.size)}. The limit is ${formatBytes(LEASE_PDF_MAX_BYTES)}.`,
      );
      return;
    }
    if (file.type !== LEASE_PDF_ACCEPT) {
      setPreExistingFileError(`${file.type || 'That file type'} is not accepted. Use a PDF.`);
      return;
    }
    setPreExistingFile(file);
  }

  const { data: unitsData, isLoading: unitsLoading } = useList<UnitOption>({
    resource: 'units',
    pagination: { current: 1, pageSize: 200 },
    filters: [{ field: 'availableForLease', operator: 'eq', value: true }],
  });
  const { data: tenantsData, isLoading: tenantsLoading } = useList<TenantOption>({
    resource: 'tenants',
    pagination: { current: 1, pageSize: 200 },
  });

  const units = unitsData?.data ?? [];
  const tenants = tenantsData?.data ?? [];
  const primaryTenantId = tenantIds[0] ?? null;
  const primaryTenant = tenants.find((t) => t.id === primaryTenantId) ?? null;

  const cap = useMemo(
    () => (monthlyRent ? maxSecurityDeposit(monthlyRent).toFixed(2) : null),
    [monthlyRent],
  );

  function toggleTenant(id: string) {
    setTenantIds((previous) =>
      previous.includes(id) ? previous.filter((t) => t !== id) : [...previous, id],
    );
  }

  // Step 2: household — local only. Nothing here is a real Dependent/Pet row
  // until "Create lease" runs, since the primary tenant's id is real but the
  // lease it's for is not, yet.
  const [dependents, setDependents] = useState<DraftDependent[]>([]);
  const [pets, setPets] = useState<DraftPet[]>([]);
  const [depName, setDepName] = useState('');
  const [depRelationship, setDepRelationship] = useState('');
  const [petName, setPetName] = useState('');
  const [petSpecies, setPetSpecies] = useState('');
  const [petBreed, setPetBreed] = useState('');

  function addDependent() {
    if (!depName.trim() || !depRelationship.trim()) return;
    setDependents((previous) => [
      ...previous,
      { name: depName.trim(), relationship: depRelationship.trim() },
    ]);
    setDepName('');
    setDepRelationship('');
  }

  function addPet() {
    if (!petName.trim() || !petSpecies.trim()) return;
    setPets((previous) => [
      ...previous,
      { name: petName.trim(), species: petSpecies.trim(), breed: petBreed.trim() },
    ]);
    setPetName('');
    setPetSpecies('');
    setPetBreed('');
  }

  // Step 3: addendums.
  const [addendums, setAddendums] = useState<LeaseAddendumOption[]>([]);
  const [addendumsLoading, setAddendumsLoading] = useState(true);
  const [selectedAddendumIds, setSelectedAddendumIds] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    api<LeaseAddendumOption[]>('/lease-addendums')
      .then((rows) => {
        if (!cancelled) setAddendums(rows);
      })
      .finally(() => {
        if (!cancelled) setAddendumsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function toggleAddendum(id: string) {
    setSelectedAddendumIds((previous) =>
      previous.includes(id) ? previous.filter((a) => a !== id) : [...previous, id],
    );
  }

  // Step 4: preview & create.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [createStage, setCreateStage] = useState<string | null>(null);
  const [createError, setCreateError] = useState<unknown>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  function buildTermsPayload() {
    return {
      unitId,
      status,
      startDate,
      endDate: endDate || null,
      monthlyRent,
      securityDeposit,
      rentDueDay: Number(rentDueDay),
      tenantIds,
      notes: notes || null,
    };
  }

  async function generatePreview() {
    setPreviewBusy(true);
    setPreviewError(null);
    try {
      const blob = await fetchPreviewPdf({
        ...buildTermsPayload(),
        addendumIds: selectedAddendumIds,
        dependents,
        pets: pets.map((p) => ({ name: p.name, species: p.species, breed: p.breed || null })),
      });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (caught) {
      setPreviewError(caught);
    } finally {
      setPreviewBusy(false);
    }
  }

  async function createEverything() {
    setCreateBusy(true);
    setCreateError(null);
    let createdLeaseId: string | null = null;
    try {
      setCreateStage('Creating the lease…');
      const lease = await api<{ id: string }>('/leases', {
        method: 'POST',
        body: JSON.stringify({
          ...buildTermsPayload(),
          isPreExisting,
          deferFirstMonthRent,
          openingBalanceAmount:
            canWriteLedger && openingBalanceAmount ? openingBalanceAmount : null,
        }),
      });
      createdLeaseId = lease.id;

      if (primaryTenantId && (dependents.length > 0 || pets.length > 0)) {
        setCreateStage('Adding household members…');
        for (const dependent of dependents) {
          await api('/dependents', {
            method: 'POST',
            body: JSON.stringify({
              tenantId: primaryTenantId,
              name: dependent.name,
              relationship: dependent.relationship,
            }),
          });
        }
        for (const pet of pets) {
          await api('/pets', {
            method: 'POST',
            body: JSON.stringify({
              tenantId: primaryTenantId,
              name: pet.name,
              species: pet.species,
              breed: pet.breed || null,
            }),
          });
        }
      }

      if (selectedAddendumIds.length > 0) {
        setCreateStage('Attaching addendums…');
        await api(`/leases/${lease.id}/addendums`, {
          method: 'POST',
          body: JSON.stringify({ addendumIds: selectedAddendumIds }),
        });
      }

      if (preExistingFile) {
        setCreateStage('Uploading the lease document…');
        await uploadDocument(preExistingFile, {
          leaseId: lease.id,
          documentType: 'LEASE',
        });
      }

      // A pre-existing lease already has its real signed contract, uploaded
      // above — the wizard's own placeholder-generated PDF would only be
      // confusing next to it.
      if (!isPreExisting) {
        setCreateStage('Generating the lease document…');
        await api(`/leases/${lease.id}/generate-document`, { method: 'POST' });
      }

      void navigate(`/staff/leases/${lease.id}`);
    } catch (caught) {
      if (createdLeaseId) {
        const message = caught instanceof Error ? caught.message : 'That step failed.';
        window.alert(
          `The lease itself was created, but this step failed: ${message}\n\n` +
            "Taking you to the lease now — finish anything missing from there (the tenant's " +
            'page for household members, Files for the document).',
        );
        void navigate(`/staff/leases/${createdLeaseId}`);
      } else {
        setCreateError(caught);
      }
    } finally {
      setCreateBusy(false);
      setCreateStage(null);
    }
  }

  const errors = fieldErrors(previewError);

  return (
    <section className="stack">
      <div className="row">
        <h1>New lease</h1>
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/staff/leases')}
        >
          ← All leases
        </button>
      </div>

      <div className="wizard-steps">
        {STEPS.map((label, index) => {
          const n = (index + 1) as 1 | 2 | 3 | 4;
          const className =
            n === step
              ? 'wizard-step wizard-step--active'
              : n < step
                ? 'wizard-step wizard-step--done'
                : 'wizard-step';
          return (
            <div key={label} className={className}>
              <span className="wizard-step__number">{index + 1}</span>
              {label}
            </div>
          );
        })}
      </div>

      {step === 1 ? (
        <form
          className="card stack"
          onSubmit={(event) => {
            event.preventDefault();
            setStep(2);
          }}
        >
          <Field label="Unit" name="unitId" required error={errors.unitId}>
            <select
              id="unitId"
              name="unitId"
              className="field__input"
              value={unitId}
              onChange={(event) => setUnitId(event.target.value)}
              required
              disabled={unitsLoading}
            >
              <option value="">{unitsLoading ? 'Loading units…' : 'Select a unit'}</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.label} · {unit.propertyName ?? unit.id} ({unit.status})
                </option>
              ))}
            </select>
          </Field>

          <Field label="Status" name="status" required error={errors.status}>
            <Select name="status" value={status} onChange={setStatus} options={LEASE_STATUSES} />
          </Field>

          <Field label="Start date" name="startDate" required error={errors.startDate}>
            <TextInput
              name="startDate"
              type="date"
              value={startDate}
              onChange={setStartDate}
              required
            />
          </Field>

          <Field
            label="End date"
            name="endDate"
            hint="Leave blank for month-to-month."
            error={errors.endDate}
          >
            <TextInput name="endDate" type="date" value={endDate} onChange={setEndDate} />
          </Field>

          <Field label="Monthly rent" name="monthlyRent" required error={errors.monthlyRent}>
            <TextInput
              name="monthlyRent"
              type="number"
              step="0.01"
              min="0.01"
              value={monthlyRent}
              onChange={setMonthlyRent}
              required
            />
          </Field>

          <Field
            label="Security deposit"
            name="securityDeposit"
            required
            hint={
              cap
                ? `Michigan caps this at 1.5× rent — $${cap} for this lease (MCL 554.602).`
                : 'Michigan caps this at 1.5× monthly rent (MCL 554.602).'
            }
            error={errors.securityDeposit}
          >
            <TextInput
              name="securityDeposit"
              type="number"
              step="0.01"
              min="0"
              value={securityDeposit}
              onChange={setSecurityDeposit}
              required
            />
          </Field>

          <Field label="Rent due day" name="rentDueDay" error={errors.rentDueDay}>
            <TextInput
              name="rentDueDay"
              type="number"
              min="1"
              max="31"
              value={rentDueDay}
              onChange={setRentDueDay}
            />
          </Field>

          <Field
            label="Tenants"
            name="tenantIds"
            required
            hint="Everyone named is jointly liable. The first one checked is the primary tenant — pets and dependents in the next step attach to them."
            error={errors.tenantIds}
          >
            <div className="stack" id="tenantIds">
              {tenantsLoading ? <span className="muted">Loading tenants…</span> : null}
              {tenants.map((tenant) => (
                <label key={tenant.id} className="row">
                  <input
                    type="checkbox"
                    checked={tenantIds.includes(tenant.id)}
                    onChange={() => toggleTenant(tenant.id)}
                  />
                  <span>
                    {tenant.firstName} {tenant.lastName}
                    {tenantIds[0] === tenant.id ? ' (primary)' : ''}
                  </span>
                </label>
              ))}
            </div>
          </Field>

          <Field label="Notes" name="notes" error={errors.notes}>
            <textarea
              id="notes"
              name="notes"
              className="field__input"
              rows={4}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </Field>

          <label className="row">
            <input
              type="checkbox"
              checked={isPreExisting}
              onChange={(event) => {
                setIsPreExisting(event.target.checked);
                if (!event.target.checked) {
                  setPreExistingFile(null);
                  setPreExistingFileError(null);
                  setOpeningBalanceAmount('');
                  setDeferFirstMonthRent(false);
                  if (preExistingFileInputRef.current) preExistingFileInputRef.current.value = '';
                }
              }}
            />
            <span>This is a pre-existing lease</span>
          </label>

          {isPreExisting ? (
            <div className="card stack" style={{ marginLeft: '1.5rem' }}>
              <Field
                label="Lease document"
                name="preExistingFile"
                hint="The real, already-signed lease — PDF, up to 25MB. Uploaded once the lease is created."
                error={preExistingFileError ?? undefined}
              >
                <input
                  ref={preExistingFileInputRef}
                  id="preExistingFile"
                  name="preExistingFile"
                  className="field__input"
                  type="file"
                  accept={LEASE_PDF_ACCEPT}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) handlePreExistingFile(file);
                  }}
                />
              </Field>
              {preExistingFile ? <p className="muted">{preExistingFile.name}</p> : null}

              <Field
                label="Balance currently owed"
                name="openingBalanceAmount"
                hint={
                  canWriteLedger
                    ? 'Recorded as a separate opening-balance charge on the lease ledger. Leave blank if nothing is owed yet.'
                    : 'Recording an opening balance requires accounting permissions.'
                }
              >
                <TextInput
                  name="openingBalanceAmount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={openingBalanceAmount}
                  onChange={setOpeningBalanceAmount}
                  disabled={!canWriteLedger}
                />
              </Field>

              <label className="row">
                <input
                  type="checkbox"
                  checked={deferFirstMonthRent}
                  onChange={(event) => setDeferFirstMonthRent(event.target.checked)}
                />
                <span>
                  Start charging rent next calendar month — check this if the balance above already
                  includes this month's rent, so the tenant isn't billed for it twice.
                </span>
              </label>
            </div>
          ) : null}

          <div className="row">
            <button type="submit" className="button" disabled={!unitId || tenantIds.length === 0}>
              Next: Household
            </button>
          </div>
        </form>
      ) : null}

      {step === 2 ? (
        <div className="card stack">
          <h2>Household</h2>
          {primaryTenant ? (
            <p className="muted">
              Pets and dependents added here will be attached to {primaryTenant.firstName}{' '}
              {primaryTenant.lastName}'s tenant profile once this lease is created.
            </p>
          ) : (
            <p className="alert alert--error">Go back and select at least one tenant first.</p>
          )}

          {primaryTenant ? (
            <>
              <h3>Dependents</h3>
              {dependents.length === 0 ? (
                <p className="muted">None added yet.</p>
              ) : (
                dependents.map((dependent, index) => (
                  <div key={`${dependent.name}-${index}`} className="row">
                    <span>
                      {dependent.name} — {dependent.relationship}
                    </span>
                    <span className="shell__spacer" />
                    <button
                      type="button"
                      className="button button--quiet"
                      onClick={() =>
                        setDependents((previous) => previous.filter((_, i) => i !== index))
                      }
                    >
                      Remove
                    </button>
                  </div>
                ))
              )}
              <div className="row">
                <input
                  className="field__input"
                  placeholder="Name"
                  value={depName}
                  onChange={(event) => setDepName(event.target.value)}
                />
                <input
                  className="field__input"
                  placeholder="Relationship (e.g. Child)"
                  value={depRelationship}
                  onChange={(event) => setDepRelationship(event.target.value)}
                />
                <button type="button" className="button button--quiet" onClick={addDependent}>
                  Add dependent
                </button>
              </div>

              <h3>Pets</h3>
              {pets.length === 0 ? (
                <p className="muted">None added yet.</p>
              ) : (
                pets.map((pet, index) => (
                  <div key={`${pet.name}-${index}`} className="row">
                    <span>
                      {pet.name} — {pet.species}
                      {pet.breed ? `, ${pet.breed}` : ''}
                    </span>
                    <span className="shell__spacer" />
                    <button
                      type="button"
                      className="button button--quiet"
                      onClick={() => setPets((previous) => previous.filter((_, i) => i !== index))}
                    >
                      Remove
                    </button>
                  </div>
                ))
              )}
              <div className="row">
                <input
                  className="field__input"
                  placeholder="Name"
                  value={petName}
                  onChange={(event) => setPetName(event.target.value)}
                />
                <input
                  className="field__input"
                  placeholder="Species (e.g. Dog)"
                  value={petSpecies}
                  onChange={(event) => setPetSpecies(event.target.value)}
                />
                <input
                  className="field__input"
                  placeholder="Breed (optional)"
                  value={petBreed}
                  onChange={(event) => setPetBreed(event.target.value)}
                />
                <button type="button" className="button button--quiet" onClick={addPet}>
                  Add pet
                </button>
              </div>
            </>
          ) : null}

          <div className="row">
            <button type="button" className="button button--quiet" onClick={() => setStep(1)}>
              Back
            </button>
            <button type="button" className="button" onClick={() => setStep(3)}>
              Next: Addendums
            </button>
          </div>
        </div>
      ) : null}

      {step === 3 ? (
        <div className="card stack">
          <h2>Addendums</h2>
          <p className="muted">
            Selected addendums are added to the generated lease document as their own section. This
            is a starter catalog — managing it is future work (see CLAUDE.md).
          </p>
          {addendumsLoading ? (
            <p className="muted">Loading…</p>
          ) : addendums.length === 0 ? (
            <p className="muted">No addendums on file.</p>
          ) : (
            addendums.map((addendum) => (
              <label key={addendum.id} className="row" style={{ alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={selectedAddendumIds.includes(addendum.id)}
                  onChange={() => toggleAddendum(addendum.id)}
                />
                <span className="stack" style={{ gap: '0.1rem' }}>
                  <strong>{addendum.name}</strong>
                  <span className="muted" style={{ fontSize: '0.85rem' }}>
                    {addendum.body}
                  </span>
                </span>
              </label>
            ))
          )}

          <div className="row">
            <button type="button" className="button button--quiet" onClick={() => setStep(2)}>
              Back
            </button>
            <button type="button" className="button" onClick={() => setStep(4)}>
              Next: Preview
            </button>
          </div>
        </div>
      ) : null}

      {step === 4 ? (
        <div className="card stack">
          <h2>Preview &amp; create</h2>
          <p className="muted">
            The preview is a <strong>placeholder document, not attorney-reviewed</strong> — it
            exercises the fill-in-and-print mechanism, not final lease language. Nothing is saved
            until you click "Create lease" below.
          </p>

          <div className="row">
            <button
              type="button"
              className="button button--quiet"
              disabled={previewBusy}
              onClick={() => void generatePreview()}
            >
              {previewBusy ? 'Generating…' : 'Generate preview'}
            </button>
          </div>
          {previewError ? <FormErrors error={previewError} /> : null}

          {previewUrl ? (
            <iframe
              title="Lease preview"
              src={previewUrl}
              style={{ width: '100%', height: '32rem', border: '1px solid var(--line)' }}
            />
          ) : null}

          <div className="row">
            <button
              type="button"
              className="button button--quiet"
              disabled={createBusy}
              onClick={() => setStep(3)}
            >
              Back
            </button>
            <button
              type="button"
              className="button"
              disabled={createBusy}
              onClick={() => void createEverything()}
            >
              {createBusy ? (createStage ?? 'Creating…') : 'Create lease'}
            </button>
          </div>
          {createError ? <FormErrors error={createError} /> : null}
        </div>
      ) : null}
    </section>
  );
}
